import React, { useContext, useEffect, useState } from 'react'
import { useTheme } from 'emotion-theming'
import clsx from 'clsx'
import { Button, Link } from '@jelly/ui'
import { isBrowser } from '@jelly/utils'
import Menu from '@material-ui/core/Menu'
import { withStyles } from '@material-ui/styles'
import { makeStyles } from '@material-ui/core/styles'

import { SiteConfigContext } from '../SiteConfigProvider'
import FeaturedTopics from './FeaturedTopics'
import { MegaPhone } from '../svgs/MegaPhone'
import { trackNavEvent } from './'

const useStyles = ({ hubMenu }) =>
	makeStyles(
		(theme) => ({
			AdvertiseLink: {
				display: 'flex',
				justifyContent: 'center',
				width: '100%',
				'&:hover': {
					'& svg': {
						'& g': {
							fill: theme.palette.tertiary.main,
						},
					},
				},
			},
			AdvertiseWithUs: {
				background: theme.palette.grey[100],
				bottom: '0',
				marginTop: 'auto',
				padding: '0.5rem 0',
				position: 'absolute',
				left: '0',
				textAlign: 'center',
				width: '100%',
				'& svg': {
					margin: 'auto 0.5rem auto 0',
				},
			},
			HubLink: {
				fontFamily: theme.typography.subtitle1.fontFamily,
				lineHeight: '1rem',
				padding: '1rem 0',
				textTransform: 'uppercase',
				width: '100%',
			},
			HubWrapper: {
				display: 'flex',
				width: '100%',
			},
			SeeAllButton: {
				marginBottom: '1rem',
			},
			SubHubElement: {
				margin: '0.5rem 0',
				listStyleType: 'none',
			},
			SubHubElements: {
				alignItems: 'flex-start',
				background: theme.palette.common.white,
				color: theme.palette.primary.main,
				display: 'flex',
				flexDirection: 'column',
				maxWidth: '300px',
				padding: '1.5rem',
				position: 'relative',
				width: '40%',
			},
			SubHubMenuContainer: {
				boxShadow: '0 9px 22px 0 rgba(0, 0, 0, 0.3)',
				display: 'flex',
				height: 'auto',
				maxWidth: '660px',
				width: '660px',
			},
			popover: {
				pointerEvents: 'none',
			},
		}),
		{ name: 'subHubMenu' }
	)

const StyledMenu = withStyles(
	{
		paper: {
			borderRadius: '0',
			display: 'flex',
			flexDirection: 'column',
			position: 'relative',
			height: 'auto',
			width: '660px',
			transition: 'none !important',
			pointerEvents: 'auto',
		},
	},
	{ name: 'styledMenu' }
)(Menu)

/**
 * Sub Hub Menu, displays a dropdown of sub hub links and featured topics
 *
 * @param {Array} props.featuredTopics :: array of featured topics with url paths and titles
 * @param {String} props.slug :: url slug path for current hub link
 * @param {Array} props.subHubs :: list of subHubs that will display as links under a hub
 * @param {String} props.title :: name of the current hub
 */
export function SubHubMenu({ featuredTopics = [], slug, subHubs = [], title }) {
	const [hubMenu, setHubMenu] = useState(null)
	const isTouchScreen = isBrowser() && 'ontouchstart' in document.documentElement
	const {
		subHubMenu: { buttonVariant, linkVariant },
	} = useTheme()
	const {
		siteConstants: { CONNECT_LINK = '/connect/' },
	} = useContext(SiteConfigContext)

	const handleSubMenuOpen = (event) => {
		event.preventDefault()
		setHubMenu(event.currentTarget)
	}

	const handleSubMenuClose = () => {
		setHubMenu(null)
	}

	const handleNavClick = (e, slug) => {
		trackNavEvent({ destination: slug, type: 'Hub link' })
		if (isTouchScreen) {
			handleSubMenuOpen(e)
		}
	}

	const handleButtonClick = (slug) => {
		trackNavEvent({ destination: slug, type: 'See All button' })
		window.location.href = `/${slug}`
	}

	useEffect(() => {
		window.addEventListener('scroll', () => setHubMenu(null))
		return () => {
			window.removeEventListener('scroll', () => setHubMenu(null))
		}
	})

	const classes = useStyles({ hubMenu })()

	return (
		<div className={classes.HubWrapper} onMouseEnter={handleSubMenuOpen} onMouseLeave={handleSubMenuClose}>
			<Link
				aria-label={title}
				aria-owns={!!hubMenu ? 'mouse-over-menu' : undefined}
				aria-haspopup="true"
				aria-expanded={!!hubMenu ? 'true' : 'false'}
				className={classes.HubLink}
				href={`/${slug}`}
				onClick={(e) => handleNavClick(e, slug)}
				variant="light"
			>
				{title}
			</Link>

			<StyledMenu
				anchorEl={hubMenu}
				getContentAnchorEl={null} // corrects menu position placements
				anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
				disableScrollLock={true}
				keepMounted // keep for SEO
				className={clsx(classes.popover)}
				id="mouse-over-menu"
				MenuListProps={{
					disablePadding: true,
				}}
				transformOrigin={{ horizontal: 'center', vertical: 'top' }}
				open={!!hubMenu}
			>
				{/* not displaying the menu items here is an accessibility issue, need to revisit */}
				<div className={classes.SubHubMenuContainer}>
					<ul className={classes.SubHubElements} role="menu">
						<Button
							aria-label={`See All ${title}`}
							className={classes.SeeAllButton}
							onClick={() => handleButtonClick(slug)}
							variant={buttonVariant}
						>
							See All
						</Button>
						{subHubs.map((subHub, i) => {
							return (
								<li className={classes.SubHubElement} key={`sub-hub-${i}`} role="menuitem">
									<Link
										href={`/${subHub.slug}/`}
										onClick={() => trackNavEvent({ destination: subHub.slug, type: 'SubHub link' })}
										variant={linkVariant}
									>
										{subHub.title}
									</Link>
								</li>
							)
						})}
						<div className={classes.AdvertiseWithUs}>
							<Link
								className={classes.AdvertiseLink}
								href={CONNECT_LINK}
								onClick={() => trackNavEvent({ destination: slug, type: 'AdvertiseWithUs link' })}
								variant="tertiary"
							>
								<MegaPhone />
								Advertise With Us!
							</Link>
						</div>
					</ul>
					<FeaturedTopics topics={featuredTopics} />
				</div>
			</StyledMenu>
		</div>
	)
}
